import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { X, ZoomInIcon, ZoomOut } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

/* ─── CONTROL BUTTON ─── */

export function ControlBtn({
  onClick,
  title,
  children,
}: {
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 34,
        height: 34,
        borderRadius: 99,
        border: "1.5px solid #E8E8EE",
        background: hovered ? "#1A1A2E" : "transparent",
        borderColor: hovered ? "#1A1A2E" : "#E8E8EE",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        color: hovered ? "#FFF" : "#7A7A90",
        transition: "background 0.2s, border-color 0.2s, color 0.2s",
      }}
    >
      {children}
    </button>
  );
}

/* ─── CERT MODAL ─── */

export function CertModal({
  image,
  isAr,
  onClose,
}: {
  image: string;
  isAr: boolean;
  onClose: () => void;
}) {
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const areaRef = useRef<HTMLDivElement>(null);

  const clampPos = useCallback((x: number, y: number, currentZoom: number) => {
    if (!areaRef.current) return { x, y };
    const { clientWidth: W, clientHeight: H } = areaRef.current;
    const maxX = Math.max(0, (W * (currentZoom - 1)) / 2);
    const maxY = Math.max(0, (H * (currentZoom - 1)) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  }, []);

  const changeZoom = useCallback(
    (delta: number) => {
      setZoom((prev) => {
        const next = Math.min(
          MAX_ZOOM,
          Math.max(MIN_ZOOM, Math.round((prev + delta) * 4) / 4),
        );
        if (next <= 1) setPos({ x: 0, y: 0 });
        else
          setPos((p) =>
            clampPos(p.x * (next / prev), p.y * (next / prev), next),
          );
        return next;
      });
    },
    [clampPos],
  );

  const resetZoom = () => {
    setZoom(1);
    setPos({ x: 0, y: 0 });
  };

  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      changeZoom(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
    },
    [changeZoom],
  );

  const onMouseDown = (e: React.MouseEvent) => {
    if (zoom <= 1) return;
    setDragging(true);
    dragRef.current = { x: e.clientX, y: e.clientY, tx: pos.x, ty: pos.y };
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    const nx = dragRef.current.tx + (e.clientX - dragRef.current.x);
    const ny = dragRef.current.ty + (e.clientY - dragRef.current.y);
    setPos(clampPos(nx, ny, zoom));
  };
  const endDrag = () => setDragging(false);

  const lastTouch = useRef<{ dist: number; cx: number; cy: number } | null>(
    null,
  );
  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouch.current = {
        dist: Math.hypot(dx, dy),
        cx: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        cy: (e.touches[0].clientY + e.touches[1].clientY) / 2,
      };
    }
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && lastTouch.current) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const ratio = dist / lastTouch.current.dist;
      setZoom((prev) => {
        const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev * ratio));
        if (next <= 1) setPos({ x: 0, y: 0 });
        return next;
      });
      lastTouch.current.dist = dist;
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "=" || e.key === "+") changeZoom(ZOOM_STEP);
      if (e.key === "-") changeZoom(-ZOOM_STEP);
      if (e.key === "0") resetZoom();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [changeZoom, onClose]);

  const [closeBtnHovered, setCloseBtnHovered] = useState(false);

  return (
    <motion.div
      key="backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={onClose}
      className="fixed inset-0 z-9999 flex items-center justify-center p-6 bg-[rgba(10,10,24,0.7)] backdrop-blur-[20px]"
    >
      <motion.div
        key="panel"
        initial={{ scale: 0.94, opacity: 0, y: 16 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.94, opacity: 0, y: 16 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[820px] bg-white rounded-4xl overflow-hidden border-[1.5px] border-[#E8E8EE]"
      >
        {/* ── top bar ── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b-[1.5px] border-[#E8E8EE]">
          {/* label */}
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#1A1A2E] block" />
            <span
              className="text-[11px] font-medium tracking-[0.15em] uppercase text-[#7A7A90]"
              style={{ fontFamily: "'DM Mono', monospace" }}
            >
              {isAr ? "عرض" : "Preview"}
            </span>
          </div>

          {/* zoom controls */}
          <div className="flex items-center gap-1.5">
            <ControlBtn
              onClick={() => changeZoom(-ZOOM_STEP)}
              title={isAr ? "تصغير" : "Zoom out"}
            >
              <ZoomOut size={14} />
            </ControlBtn>

            <span
              className="text-[11px] font-medium text-[#7A7A90] min-w-[42px] text-center tracking-[0.05em]"
              style={{ fontFamily: "'DM Mono', monospace" }}
            >
              {Math.round(zoom * 100)}%
            </span>

            <ControlBtn
              onClick={() => changeZoom(ZOOM_STEP)}
              title={isAr ? "تكبير" : "Zoom in"}
            >
              <ZoomInIcon size={14} />
            </ControlBtn>

            <ControlBtn
              onClick={resetZoom}
              title={isAr ? "إعادة ضبط" : "Reset zoom"}
            >
              <span
                className="text-[10px] font-semibold tracking-[-0.03em]"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                1:1
              </span>
            </ControlBtn>

            {/* divider */}
            <div className="w-px h-[18px] bg-[#E8E8EE] mx-1" />

            {/* close */}
            <button
              onClick={onClose}
              onMouseEnter={() => setCloseBtnHovered(true)}
              onMouseLeave={() => setCloseBtnHovered(false)}
              className={cn(
                "w-[34px] h-[34px] rounded-full border-[1.5px] flex items-center justify-center cursor-pointer transition-all duration-200",
                closeBtnHovered
                  ? "bg-[#1A1A2E] border-[#1A1A2E] text-white"
                  : "bg-transparent border-[#E8E8EE] text-[#7A7A90]",
              )}
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* ── image area ── */}
        <div
          ref={areaRef}
          className={cn(
            "relative h-[68vh] bg-[#F7F7FB] overflow-hidden select-none",
            zoom > 1
              ? dragging
                ? "cursor-grabbing"
                : "cursor-grab"
              : "cursor-default",
          )}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={() => {
            lastTouch.current = null;
          }}
        >
          {/* zoom hint */}
          <AnimatePresence>
            {zoom === 1 && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                transition={{ duration: 0.2 }}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 bg-[rgba(26,26,46,0.7)] backdrop-blur-sm rounded-full px-3.5 py-1.5 pointer-events-none"
              >
                <span
                  className="text-[10px] text-white/75 tracking-[0.08em] uppercase"
                  style={{ fontFamily: "'DM Mono', monospace" }}
                >
                  {isAr
                    ? "استخدم عجلة الفأرة للتكبير"
                    : "Scroll or pinch to zoom"}
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* transformed image */}
          <div
            className="absolute inset-0 flex items-center justify-center will-change-transform"
            style={{
              transform: `translate(${pos.x}px, ${pos.y}px) scale(${zoom})`,
              transition: dragging
                ? "none"
                : "transform 0.18s cubic-bezier(0.22,1,0.36,1)",
            }}
          >
            <Image
              src={image}
              alt="Certificate"
              fill
              className="object-contain p-10 select-none pointer-events-none"
              draggable={false}
            />
          </div>
        </div>

        {/* ── keyboard hint ── */}
        <div className="flex items-center justify-center gap-4 px-5 py-2.5 border-t-[1.5px] border-[#E8E8EE]">
          {[
            { key: "+  /  −", label: isAr ? "تكبير / تصغير" : "Zoom in / out" },
            { key: "0", label: isAr ? "إعادة ضبط" : "Reset" },
            { key: "Esc", label: isAr ? "إغلاق" : "Close" },
          ].map(({ key, label }) => (
            <div
              key={key}
              className="flex items-center gap-1.5 text-[11px] text-[#AAAABC]"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              <kbd
                className="text-[10px] bg-[#F3F3F7] border border-[#E8E8EE] rounded-md px-[7px] py-0.5 text-[#7A7A90]"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                {key}
              </kbd>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}
